import React, { useState, useEffect } from 'react';
import { GamingService } from '../services/gaming';

const SkillTreeViewer = () => {
  const [skills, setSkills] = useState({ offensive: [], defensive: [], support: [] });
  const [progression, setProgression] = useState(null);
  const [selectedTree, setSelectedTree] = useState('offensive');
  const [selectedSkill, setSelectedSkill] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [skillsData, progressionData] = await Promise.all([
        GamingService.getAllSkills(),
        GamingService.getProgression()
      ]);
      setSkills(skillsData);
      setProgression(progressionData);
    } catch (error) {
      console.error('Error loading skill tree:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUnlockSkill = async (skillId) => {
    try {
      const result = await GamingService.unlockSkill(skillId);
      alert(`Skill unlocked! Points remaining: ${result.points_remaining}`);
      await loadData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to unlock skill');
    }
  };

  const getSkillRank = (skillId) => {
    return progression?.skill_tree?.unlocked_skills?.[skillId] || 0;
  };

  const canUnlockSkill = (skill) => {
    if (!progression) return false;
    
    const currentRank = getSkillRank(skill.id);
    if (currentRank >= skill.max_rank) return false;
    
    if (progression.level < skill.required_level) return false;
    
    if (progression.skill_tree.available_points < skill.cost) return false;
    
    for (const prereqId of skill.prerequisites) {
      if (!progression.skill_tree.unlocked_skills[prereqId]) {
        return false;
      }
    }
    
    return true;
  };

  const getTreeColor = (tree) => {
    switch (tree) {
      case 'offensive': return 'from-red-500 to-orange-500';
      case 'defensive': return 'from-blue-500 to-cyan-500';
      case 'support': return 'from-green-500 to-emerald-500';
      default: return 'from-gray-500 to-gray-600';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-black text-white">
        <div className="text-2xl">Loading skill trees...</div>
      </div>
    );
  }

  const currentSkills = skills[selectedTree] || [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black text-white p-6">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-8">
        <h1 className="text-5xl font-bold mb-4 bg-gradient-to-r from-yellow-400 to-red-500 bg-clip-text text-transparent">
          Skill Trees
        </h1>
        
        {/* Player Info */}
        {progression && (
          <div className="flex items-center gap-6 bg-gray-900/50 p-4 rounded-lg border border-yellow-500/30">
            <div>
              <span className="text-gray-400">Level:</span>
              <span className="ml-2 text-2xl font-bold text-yellow-400">{progression.level}</span>
            </div>
            <div>
              <span className="text-gray-400">Available Points:</span>
              <span className="ml-2 text-2xl font-bold text-green-400">{progression.skill_tree.available_points}</span>
            </div>
            <div>
              <span className="text-gray-400">Title:</span>
              <span className="ml-2 text-xl font-bold text-purple-400">{progression.title}</span>
            </div>
          </div>
        )}
      </div>

      {/* Tree Selector */}
      <div className="max-w-7xl mx-auto mb-8 flex gap-4">
        {['offensive', 'defensive', 'support'].map((tree) => (
          <button
            key={tree}
            onClick={() => setSelectedTree(tree)}
            className={`flex-1 py-4 px-6 rounded-lg font-bold text-xl transition-all transform hover:scale-105 ${
              selectedTree === tree
                ? `bg-gradient-to-r ${getTreeColor(tree)} text-white shadow-2xl`
                : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
            }`}
          >
            {tree.charAt(0).toUpperCase() + tree.slice(1)}
            <div className="text-sm font-normal mt-1">
              {progression?.skill_tree?.[`${tree}_points`] || 0} points spent
            </div>
          </button>
        ))}
      </div>

      {/* Skills Grid */}
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {currentSkills.map((skill) => {
            const currentRank = getSkillRank(skill.id);
            const unlocked = currentRank > 0;
            const canUnlock = canUnlockSkill(skill);
            const maxed = currentRank >= skill.max_rank;

            return (
              <div
                key={skill.id}
                className={`p-6 rounded-xl border-2 transition-all transform hover:scale-105 cursor-pointer ${
                  unlocked
                    ? 'bg-gray-800/80 border-yellow-500'
                    : 'bg-gray-900/50 border-gray-700'
                } ${canUnlock ? 'shadow-lg shadow-green-500/50' : ''}`}
                onClick={() => setSelectedSkill(skill)}
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="px-3 py-1 bg-gray-800 rounded-full text-xs font-bold">
                    Tier {skill.tier}
                  </span>
                  <span className="text-xs text-gray-400">
                    Level {skill.required_level}+
                  </span>
                </div>

                <h3 className="text-2xl font-bold mb-2">{skill.name}</h3>

                <div className="mb-3">
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-gray-400">Rank</span>
                    <span className={maxed ? 'text-yellow-400 font-bold' : 'text-white'}>
                      {currentRank} / {skill.max_rank}
                    </span>
                  </div>
                  <div className="w-full bg-gray-800 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full bg-gradient-to-r ${getTreeColor(selectedTree)}`}
                      style={{ width: `${(currentRank / skill.max_rank) * 100}%` }}
                    />
                  </div>
                </div>

                <p className="text-gray-300 text-sm mb-4">{skill.description}</p>

                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-400">
                    Cost: <span className="text-yellow-400 font-bold">{skill.cost}</span> points
                  </span>
                  
                  {!maxed && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleUnlockSkill(skill.id);
                      }}
                      disabled={!canUnlock}
                      className={`px-4 py-2 rounded-lg font-bold text-sm transition-all ${
                        canUnlock
                          ? `bg-gradient-to-r ${getTreeColor(selectedTree)} hover:shadow-lg`
                          : 'bg-gray-700 text-gray-500 cursor-not-allowed'
                      }`}
                    >
                      {unlocked ? 'Upgrade' : 'Unlock'}
                    </button>
                  )}
                  
                  {maxed && (
                    <span className="px-4 py-2 bg-yellow-500/20 text-yellow-400 rounded-lg font-bold text-sm">
                      MAXED
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default SkillTreeViewer;
